import type { AssetManifest } from '@kenocyber/shared';
import Phaser from 'phaser';
import { DEV_TOOLS_ALLOWED } from '../dev-tools';
import type { GameBridge, HuntSessionMessage } from './bridge';
import { HuntScene } from './HuntScene';
import { MiningScene } from './MiningScene';
import { OpenWorldScene } from './OpenWorldScene';
import { PvpScene } from './PvpScene';

/**
 * The ONE place a `Phaser.Game` is built.
 *
 * ## Why it was lifted out of `PhaserHost`
 *
 * The visual benchmark needs the same game the product boots - same renderer,
 * same canvas, same transparency, same scale mode, same three scenes in the
 * same order, same teardown - and the only honest way to get it is to call the
 * same code. A harness that copied the configuration would measure a game that
 * merely resembles the product, and would go on resembling it right up to the
 * day somebody changed one of them.
 *
 * So there is exactly one construction site. `apps/web/src/game/create-game.test.ts`
 * asserts that `new Phaser.Game(` appears once in the whole of `apps/web/src`,
 * which is what makes "one factory" a property rather than a convention.
 *
 * ## What it does NOT do
 *
 * It does not decide when a game exists. `PhaserHost` still owns that for the
 * product - one game per mounted host, never recreated by a React re-render -
 * and the benchmark owns it for itself. This file owns the SHAPE.
 */

export interface KenocyberGameOptions {
  /** The element the canvas is parented to, and whose size it follows. */
  readonly container: HTMLElement;
  readonly bridge: GameBridge;
  readonly manifest: AssetManifest;
  readonly debug: boolean;
  readonly bossFast: boolean;
  /** DEV ONLY - very high cadence, still drawn as discrete projectiles. */
  readonly rapidFire: boolean;
  /** Server-authorised session; the hunt is built from this and nothing else. */
  readonly session: HuntSessionMessage;
}

/**
 * The configuration, as a value.
 *
 * Separated from the construction so a test can read every field without a
 * WebGL context - the renderer, the scale mode, the background, and above all
 * the scene list and its order.
 */
export function kenocyberGameConfig(options: KenocyberGameOptions): Phaser.Types.Core.GameConfig {
  const { container, bridge, manifest, debug, bossFast, rapidFire, session } = options;
  return {
    type: Phaser.AUTO,
    parent: container,
    backgroundColor: '#04040a',
    scale: {
      // RESIZE, not FIT: the canvas always matches the container exactly, so
      // there are never letterbox bands. The scene then applies a uniform
      // camera zoom and centres the 1280x720 logical arena inside it, and
      // whatever room is left over becomes visible space that the starfield
      // fills. FIT would have preserved the aspect ratio by padding instead.
      mode: Phaser.Scale.RESIZE,
      width: '100%',
      height: '100%',
    },
    /*
     * THREE scenes, ONE game.
     *
     * The mining ring and the duel are handed to the same `Phaser.Game` at
     * boot, so there is exactly one canvas and one WebGL context for the
     * life of the tab. Opening and closing either is a bridge MESSAGE that
     * changes which objects exist - never a scene start, never a renderer.
     *
     * The hunt is listed first so it draws first; the others layer over it.
     * The ring leaves the hunt ticking behind it because it is a panel over
     * a running game. A duel does not: React suspends the hunt before it
     * sends `pvp:open`, through the same `ui:set-paused` every menu uses.
     */
    scene: [
      new HuntScene({ bridge, manifest, debug, bossFast, rapidFire, session }),
      new MiningScene({ bridge, manifest }),
      new PvpScene({ bridge, manifest }),
      /*
       * O quadrante do mundo aberto. Uma quarta cena no mesmo jogo, pela mesma
       * razão que a mina é a terceira: um segundo `Phaser.Game` seria um segundo
       * contexto WebGL, e o que muda entre modos é quais objetos existem.
       */
      new OpenWorldScene({ bridge, manifest }),
    ],
  };
}

/** The order the three scenes are handed to Phaser, and therefore draw in. */
export const KENOCYBER_SCENE_ORDER: readonly string[] = Object.freeze([
  HuntScene.KEY,
  MiningScene.KEY,
  PvpScene.KEY,
]);

export interface KenocyberGame {
  readonly game: Phaser.Game;
  /** Undoes everything {@link createKenocyberGame} did, once. */
  destroy(): void;
}

/**
 * Boots the game and wires the lifecycle every caller must have.
 *
 * The three things bundled here are the three that were easy to forget and
 * expensive to forget: the container observer, the inspection handle's guard,
 * and a teardown that undoes both before destroying the game.
 */
export function createKenocyberGame(options: KenocyberGameOptions): KenocyberGame {
  const game = new Phaser.Game(kenocyberGameConfig(options));

  /*
   * The running game, reachable for INSPECTION only, and NEVER in production.
   *
   * A browser test has no other way to ask "is there one canvas, is the
   * mining scene on the same game, is every hull facing the core" - the
   * alternative is a screenshot and an opinion, and an opinion does not
   * notice a scene that silently drew nothing.
   *
   * Guarded by the build constant directly, not through a variable, so
   * `vite build` folds the branch away and `dist` never defines the handle.
   * It is inspection rather than capability, but the rule does not grade
   * debug hooks on how harmless they look: a handle that only tests need is
   * a handle production does not ship.
   */
  if (DEV_TOOLS_ALLOWED) {
    (window as unknown as { __kenocyberGame?: Phaser.Game }).__kenocyberGame = game;
  }

  /*
   * The canvas follows its CONTAINER, not the window.
   *
   * `Scale.RESIZE` only listens for window resize events, so a container
   * that changes size while the window does not leaves the canvas at its old
   * size - and because the host centres it, the difference shows as a band
   * of dead space above and below the arena.
   *
   * That is not hypothetical here. The MENU button folds the tab row away,
   * which changes the height of the top bar with no window event at all, and
   * the play area is whatever is left under the bar. It was found the other
   * way round: at 1366x768 a chip label wrapped, the bar was born fifteen
   * pixels taller than the canvas had been sized for, and the E2E check for
   * letterbox gutters failed at that width while passing at 1920.
   *
   * The wrap itself is fixed in the stylesheet. This is the general answer:
   * whatever moves the bar, the arena resizes with it.
   */
  const observer = new ResizeObserver(([entry]) => {
    const box = entry?.contentRect;
    // Zero happens while the element is detached, and resizing to nothing
    // would drop the renderer's framebuffer for no reason.
    if (!box || box.width < 1 || box.height < 1) {
      return;
    }
    game.scale.resize(box.width, box.height);
  });
  observer.observe(options.container);

  let destroyed = false;
  return {
    game,
    destroy() {
      // Idempotent: React's cleanup and a harness's own teardown can both fire.
      if (destroyed) {
        return;
      }
      destroyed = true;
      observer.disconnect();
      if (DEV_TOOLS_ALLOWED) {
        delete (window as unknown as { __kenocyberGame?: Phaser.Game }).__kenocyberGame;
      }
      game.destroy(true);
    },
  };
}
